import React, {
  Key,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Input,
  Button,
  Tooltip,
  message,
  List,
  Typography,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';

import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';

import { ControlOutlined, PlusCircleOutlined } from '@ant-design/icons';
import api from '@services/api';
import AdminActions from '@components/AdminActions';
import rolesConstants from '@utils/rolesConstants';
import SessionContext from '@providers/Session/SessionContext';
import useSearchParams from '@hooks/useSearchParams';
import ActionsModalByTypes from '@components/Actions/ActionsModal/ActionsModalByTypes';
import RequestReport, { ReportItemsProps } from '@components/RequestReport';
import { ColumnType } from 'antd/lib/table';
import { formatCpfCnpj } from '@utils/formatters';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import RequestCancelReports from './RequestCancelReports';
import FinancialBlockStatusColumn from './FinancialBlockStatusColumn';
import CustomerModal from './CustomerViewerModal';
import BatchPermissionsModal from './BatchPermissionsModal';
import * as Styled from './styles';
import { ICustomer, ICustomersData } from '../types';
import './styles.less';

const { Search } = Input;
interface ICustomerProps {
  isMotBusiness?: boolean;
}
const Customers: React.FC<ICustomerProps> = ({ isMotBusiness }) => {
  const { t } = useTranslation(['customers', 'attributes']);
  const { keycloak } = useContext(KeycloakContext);
  const customerType = keycloak.idTokenParsed?.customerType;
  const { effectiveRoles, isAllcom, customer, selectedApp } =
    useContext(SessionContext);
  const rolesToAccess = effectiveRoles?.portal;
  const isMotEnterprise = selectedApp === ApplicationsConstants.MOT_ENTERPRISE;

  const haveBatchPermissionsAction =
    rolesToAccess?.indexOf(rolesConstants.BATCH_PERMISSIONS) !== -1;

  const haveBatchPermissionFinancial =
    rolesToAccess?.indexOf(rolesConstants.FINANCIAL_LOCK_UNLOCK) !== -1;
  const customerCanManage =
    rolesToAccess?.indexOf(
      isMotBusiness
        ? rolesConstants.MANAGE_DEPARTMENTS
        : rolesConstants.MANAGE_CUSTOMERS,
    ) !== -1;

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [customerId, setCustomerId] = useState<number>(-1);
  const [nameValue, setNameValue] = useState('');
  const [cpfCnpjValue, setCpfCnpjValue] = useState('');
  const [showActionsModal, setShowActionsModal] = useState<boolean>(false);
  const [tableSelection, setTableSelection] =
    useState<ICustomersData['content']>();
  const [showRequestReport, setShowRequestReport] = useState(false);

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: ['name', 'cpfCnpj', 'dueDay', 'financialBlock'],
  });

  const { name, cpfCnpj, dueDay, financialBlock, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );

  const { data: customersData, mutate } = useSwr<ICustomersData>(
    '/service-proxy/broker/customers',
    {
      params: {
        page: page ? Number(page) - 1 : 0,
        linesPerPage: linesPerPage ?? 20,
        dueDay,
        financialBlock,
        name,
        cpfCnpj,
      },
    },
  );

  useEffect(() => {
    setNameValue(name);
  }, [name]);

  useEffect(() => {
    setCpfCnpjValue(cpfCnpj);
  }, [cpfCnpj]);

  const handleMutateFinancialBlock = useCallback(
    async (cstId: number, financialBlockMutate: boolean) => {
      await mutate({
        ...customersData,
        // @ts-ignore
        content: customersData?.content?.map(cst =>
          cst.id === cstId
            ? {
                ...cst,
                financialBlock: !financialBlockMutate,
              }
            : cst,
        ),
      });
    },
    [customersData, mutate],
  );

  const handleMutateCustomers = useCallback(
    (type: 'CREATE' | 'DELETE', data: any) => {
      if (customersData) {
        if (type === 'CREATE') {
          mutate({
            ...customersData,
            content: [...customersData.content, data],
          });
        } else {
          mutate({
            ...customersData,
            content: customersData.content.filter(
              customerData => customerData.id !== data.id,
            ),
          });
        }
      }
      customer.mutateAllCustomerData();
    },
    [customersData, mutate, customer],
  );

  const handleActions = useCallback(
    async (actionType: String, record: ICustomer) => {
      // actionType = 'UPDATE' || 'DELETE'
      const isUpdate = actionType === 'UPDATE';
      if (isUpdate) {
        setCustomerId(record.id);
        setIsModalVisible(true);
      } else {
        try {
          await api.delete(`/customers/${record.id}`);
          handleMutateCustomers('DELETE', { id: record.id });
          message.success(
            isMotBusiness
              ? t('msgs.success-deleting-department')
              : t('msgs.success-deleting-customer'),
          );
        } catch {
          message.error(
            isMotBusiness
              ? t('msgs.error-deleting-department')
              : t('msgs.error-deleting-customer'),
          );
        }
      }
    },
    [t, handleMutateCustomers, isMotBusiness],
  );

  const columnApn = useCallback(
    (value: { nameApn: string; mainApn: boolean }[]) => {
      return (
        value?.length > 0 && (
          <Tooltip
            title={
              <>
                <List
                  dataSource={value}
                  renderItem={item => (
                    <List.Item style={{ padding: '18px' }}>
                      <Typography.Text>{item.nameApn}</Typography.Text>
                    </List.Item>
                  )}
                  style={{
                    maxHeight: '200px',
                    overflow: 'auto',
                    backgroundColor: 'white',
                  }}
                />
              </>
            }
            className="apn-tooltip"
          >
            <Button type="link">
              {`${value.length} ${t('attributes:apn')}`}
            </Button>
          </Tooltip>
        )
      );
    },
    [t],
  );

  const columns = useMemo((): ColumnType<any>[] => {
    const columnsDefault: ColumnType<any>[] = [
      {
        title: t('attributes:id'),
        dataIndex: 'id',
      },
      {
        title: t('attributes:name'),
        dataIndex: 'name',
      },
      {
        title: t('attributes:nickname'),
        dataIndex: 'nickname',
      },
      {
        title: t('attributes:tradingName'),
        dataIndex: 'tradingName',
      },
      {
        title: t('attributes:cpfCnpj'),
        dataIndex: 'cpfCnpj',
        render: formatCpfCnpj,
      },
      {
        title: t('attributes:apns'),
        dataIndex: 'apns',
        render: (apns: { nameApn: string; mainApn: boolean }[]) =>
          columnApn(apns),
      },
    ];

    if (!isMotBusiness) {
      columnsDefault.push({
        title: t('attributes:customerType.title'),
        dataIndex: 'type',
        render: (value: string) =>
          value ? t(`attributes:customerType.${value}`) : '',
      });
    }

    if (isMotEnterprise) {
      columnsDefault.push({
        title: t('attributes:dueDay'),
        dataIndex: 'dueDay',
        align: 'center',
        render: (value, record) => {
          const { secondDueDay } = record;

          return [value, secondDueDay]?.filter(item => item)?.join(', ');
        },
      });

      if (haveBatchPermissionFinancial) {
        columnsDefault.push({
          title: t('attributes:financialBlockStatus'),
          dataIndex: 'financialBlock',
          align: 'center',
          render: (value: boolean, record: any) => (
            <FinancialBlockStatusColumn
              value={value}
              customerId={record.id}
              mutate={() => handleMutateFinancialBlock(record.id, value)}
            />
          ),
        });
      }
    }

    if (customerCanManage) {
      columnsDefault.push({
        title: '',
        dataIndex: 'actions',
        width: 100,
        render: (text: string, record: ICustomer) => (
          <AdminActions
            alertDelete={t('form.msgs.deleteAlert')}
            entityToExclude={record.name}
            ruleToNotExclude={record.id === keycloak.idTokenParsed?.customerId}
            onEdit={() => handleActions('UPDATE', record)}
            onDelete={() => handleActions('DELETE', record)}
          />
        ),
      });
    }

    return columnsDefault;
  }, [
    t,
    isMotBusiness,
    customerCanManage,
    isMotEnterprise,
    haveBatchPermissionFinancial,
    keycloak.idTokenParsed?.customerId,
    handleActions,
    columnApn,
    handleMutateFinancialBlock,
  ]);

  const onClose = useCallback(() => {
    setIsModalVisible(false);
    setCustomerId(-1);
  }, []);

  const handleNewCustomer = useCallback(() => {
    setCustomerId(-1);
    setIsModalVisible(true);
  }, []);

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:name'),
        element: (
          <Search
            onSearch={value => setSearchParams({ name: value })}
            onChange={({ target: { value } }) => setNameValue(value)}
            value={nameValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:cpfCnpj'),
        element: (
          <Search
            onSearch={value => setSearchParams({ cpfCnpj: value })}
            onChange={({ target: { value } }) => setCpfCnpjValue(value)}
            value={cpfCnpjValue}
            allowClear
          />
        ),
      },
      {
        visible: isMotEnterprise,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:dueDay'),
        element: (
          <Select
            options={[{ value: 15 }, { value: 20 }, { value: 25 }]}
            value={dueDay}
            style={{ width: '100%' }}
            onChange={value => {
              setSearchParams({ dueDay: value !== 0 ? value : undefined });
            }}
            allowClear
          />
        ),
      },
      {
        visible: isMotEnterprise,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:financialBlockStatus'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: t('actions.financialBlock.blocked'),
                value: 'true',
              },
              {
                label: t('actions.financialBlock.unlocked'),
                value: 'false',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={financialBlock}
            onChange={value => {
              setSearchParams({
                financialBlock: value,
              });
            }}
          />
        ),
      },
    ],
    [
      cpfCnpjValue,
      dueDay,
      financialBlock,
      isMotEnterprise,
      nameValue,
      setSearchParams,
      t,
    ],
  );

  const reportItems = useMemo(
    (): ReportItemsProps[] => [
      {
        role: rolesConstants.REQUEST_CUSTOMER_REPORT,
        params: {
          name,
          cpfCnpj,
        },
        method: 'get',
        path: '/service-proxy/broker/customers/report',
        type: 'CUSTOMERS',
      },
      {
        role: rolesConstants.MANAGEMENT_REPORT,
        method: 'get',
        path: '/service-proxy/inventory/MOT/management-report',
        type: 'MANAGEMENT',
      },
      {
        role: rolesConstants.MANAGEMENT_CANCELLATION_REPORT,
        method: 'get',
        path: '/service-proxy/broker/cancellation/report',
        type: 'CANCELLATION_MANAGEMENT',
        action: () => setShowRequestReport(true),
      },
    ],
    [cpfCnpj, name],
  );

  const extraActions = useMemo(
    () => (
      <>
        {customerCanManage && !isAllcom && (
          <Tooltip
            placement="top"
            title={
              isMotBusiness
                ? t('modal.title.newDepartment')
                : t('modal.title.newCustomer')
            }
          >
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => handleNewCustomer()}
            />
          </Tooltip>
        )}

        {isMotEnterprise && <RequestReport items={reportItems} />}

        {haveBatchPermissionsAction && (
          <BatchPermissionsModal
            selectedCustomers={tableSelection}
            isMotBusiness={Boolean(isMotBusiness)}
          />
        )}

        {(customerType === 'BROKER' || customerType === 'SUPERBROKER') &&
          haveBatchPermissionFinancial &&
          isMotEnterprise && (
            <Tooltip
              title={t('device-actions:labels.mot.financial-lock-unlock')}
            >
              <Button
                icon={
                  <ControlOutlined
                    style={{
                      fontSize: '1.185rem',
                    }}
                  />
                }
                onClick={() => {
                  setShowActionsModal(true);
                }}
              />
            </Tooltip>
          )}
      </>
    ),
    [
      customerCanManage,
      isAllcom,
      isMotBusiness,
      t,
      isMotEnterprise,
      reportItems,
      haveBatchPermissionsAction,
      tableSelection,
      customerType,
      haveBatchPermissionFinancial,
      handleNewCustomer,
    ],
  );

  return (
    <>
      {isModalVisible && (
        <CustomerModal
          customerId={customerId}
          visible={isModalVisible}
          onClose={onClose}
          mutateData={handleMutateCustomers}
          isMotBusiness={Boolean(isMotBusiness)}
        />
      )}

      <RequestCancelReports
        visible={showRequestReport}
        onClose={() => setShowRequestReport(false)}
      />

      <Styled.Container>
        <Table
          selectable
          setSelectedRowKeys={(
            _: Key[],
            customers: ICustomersData['content'],
          ) => setTableSelection(customers)}
          // @ts-ignore
          columns={columns}
          data={customersData?.content || []}
          loading={!customersData?.content}
          title={isMotBusiness ? t('departments') : t('customers')}
          tableKey="customers"
          filters={{
            list: tableFilters,
            hasValuesInFilters: hasValuesInExtraParams,
            onClearFilters: () => clearSearchParams(),
          }}
          pagination={{
            page,
            linesPerPage,
            total: customersData?.totalElements,
          }}
          onTableChange={setSearchParams}
          extraActions={extraActions}
        />
      </Styled.Container>

      <ActionsModalByTypes.Simcard
        visible={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        actionRole={rolesConstants.FINANCIAL_LOCK_UNLOCK}
        selectedDevices={[]}
        isUpload
      />
    </>
  );
};

export default Customers;
